import { I18nWording, useDatabaseTranslation } from '@/services/i18n';

import type { ProductAttributeValue } from './productAttribute';

export type GetSizeOptionsParams = {
  article: {
    product?: { sizes?: I18nWording[] | null } | null;
    productAttributeValues?: ProductAttributeValue[];
    snapshot: {
      articleExchange?: {
        replacementProduct?: {
          sizes?: I18nWording[] | null;
        } | null;
      } | null;
    };
  };
  isReplacementProduct?: boolean;
};

const sizeLettersOrdered = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL'];
const typePriority: Record<SizeType, number> = { letter: 0, number: 1, other: 2 };

type SizeType = 'letter' | 'number' | 'other';
type SizeTypeAndOrderInfo =
  { type: 'letter'; order: number } | { type: 'number'; order: number } | { type: 'other' };

const getSizeOptions = ({ article, isReplacementProduct }: GetSizeOptionsParams) => {
  if (isReplacementProduct) {
    return article.snapshot.articleExchange?.replacementProduct?.sizes ?? [];
  }

  const sizesForSelectedProduct = article.product?.sizes;
  const sizesForSelectedProductAttributes = [...(article.productAttributeValues ?? [])]
    .reverse()
    .find((productAttributeValue) => !!productAttributeValue.sizes?.length)?.sizes;

  return sizesForSelectedProduct ?? sizesForSelectedProductAttributes ?? [];
};

export const hasSizeOptions = (params: GetSizeOptionsParams) => {
  return !!getSizeOptions(params).length;
};

const formatSize = (size: string): string => {
  return size.trim().toUpperCase();
};
const getSizeType = (sizeType: string): SizeType => {
  const formattedSize = formatSize(sizeType);

  const isStandardNumber = !isNaN(Number(formattedSize)); //JSON string can be a number
  const isGreaterThan = /^>\s*\d+$/.test(formattedSize); // regex to know if we receive >
  const isLessThan = /^<\s*\d+$/.test(formattedSize); // regex to know if we receive <

  if (sizeLettersOrdered.includes(formattedSize)) return 'letter';
  if (isStandardNumber || isGreaterThan || isLessThan) return 'number';

  return 'other';
};

const getLetterIndex = (sizeLetter: string): number => {
  return sizeLettersOrdered.indexOf(formatSize(sizeLetter));
};

const getSizeNumberOrder = (sizeNumber: string): number => {
  const sizeNumberFormatted = formatSize(sizeNumber);

  // In case we have a size like "> 45",
  // we transform it into 46 so that it's ordered after all numberSizes
  if (/^>\s*\d+$/.test(sizeNumberFormatted)) {
    return Number(sizeNumberFormatted.replace('>', '')) + 1;
  }

  // In case we have a size like "< 36",
  // we transform it into 35 so that it's ordered before all numberSizes
  if (/^<\s*\d+$/.test(sizeNumberFormatted)) {
    return Number(sizeNumberFormatted.replace('<', '')) - 1;
  }

  return Number(sizeNumberFormatted);
};

const getTypeAndOrder = (size: string): SizeTypeAndOrderInfo => {
  const type = getSizeType(size);

  switch (type) {
    case 'letter':
      return { type, order: getLetterIndex(size) };
    case 'number':
      return { type, order: getSizeNumberOrder(size) };
    case 'other':
      return { type };
  }
};

export function useSizesOptionsSorted(param: GetSizeOptionsParams) {
  const { _db } = useDatabaseTranslation();
  const compareSizes: (a: I18nWording, b: I18nWording) => number = (a, b) => {
    const aOption = getTypeAndOrder(_db(a));
    const bOption = getTypeAndOrder(_db(b));

    if (aOption.type !== bOption.type) {
      return typePriority[aOption.type] - typePriority[bOption.type];
    }
    if (aOption.type !== 'other' && bOption.type !== 'other') {
      return aOption.order - bOption.order;
    }

    return _db(a).localeCompare(_db(b));
  };

  const sortSizes = (sizeOptions: I18nWording[]): I18nWording[] => {
    // .sort() mutates the original array
    // we spread to clone it and keep the original untouched
    return [...sizeOptions].sort(compareSizes);
  };

  const sizeOptionsToSort = getSizeOptions(param);

  const sizesOptions = sortSizes(sizeOptionsToSort);

  return {
    getSortSizes: sortSizes,
    sizesOptions,
    sizeOptionValue: (sizeOptionWording: I18nWording) => _db(sizeOptionWording),
    sizeOptionLabel: (sizeOptionWording: I18nWording) => _db(sizeOptionWording).toUpperCase(),
  };
}
